import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/states';
import type { Branch } from '@/lib/shared-types';
import { useAssignBranchMutation, useRemoveBrandBranchMutation, type BrandDetail } from '../brandsApi';
import { messageOf } from '../lib';

/**
 * Which branches belong to this brand, and adding/removing one.
 *
 * `brands.manage`, not `brands.onboard` or anything a franchisor holds —
 * ADR-0023 §3's boundary-defining act: deciding who counts as "this
 * franchisor's own branches" stays a tenant-admin decision, the same way a
 * branch manager may not grant a role scoped to a branch they do not manage.
 * A franchisee branch manager (`brands.view` only) sees this list read-only.
 */
export default function BranchesPanel({ brand }: { brand: BrandDetail }) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const { confirm } = useConfirm();
  const canManage = can('brands.manage');

  const [pickedBranchId, setPickedBranchId] = useState('');
  const branchesQuery = useApi<unknown>(canManage ? '/branches' : null, { limit: 200 });
  const [assignBranch, { isLoading: assigning }] = useAssignBranchMutation();
  const [removeBranch] = useRemoveBrandBranchMutation();

  const memberIds = useMemo(() => new Set(brand.branches.map((b) => b.id)), [brand.branches]);
  const candidates = useMemo(
    () => rowsOf<Branch>(branchesQuery.data).rows.filter((b) => !memberIds.has(b.id)),
    [branchesQuery.data, memberIds],
  );

  const assign = async () => {
    if (!pickedBranchId) return;
    try {
      await assignBranch({ brandId: brand.id, branchId: pickedBranchId }).unwrap();
      toast.success(t('brands.overview.assigned'));
      setPickedBranchId('');
    } catch (e) {
      // A branch already claimed by a different brand 409s — the server's
      // own message names which case it is, so it is shown verbatim rather
      // than replaced with a generic one.
      toast.error(messageOf(e, t('brands.overview.assignFailed')));
    }
  };

  const remove = async (branch: { id: string; name: string }) => {
    const ok = await confirm({
      title: t('brands.overview.removeConfirmTitle', { name: branch.name }),
      body: t('brands.overview.removeConfirmBody'),
      confirmLabel: t('brands.overview.removeBranch'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await removeBranch({ brandId: brand.id, branchId: branch.id }).unwrap();
      toast.success(t('brands.overview.removed'));
    } catch (e) {
      toast.error(messageOf(e, t('brands.overview.removeFailed')));
    }
  };

  return (
    <div className="space-y-4">
      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle>{t('brands.overview.addBranch')}</CardTitle>
            <CardDescription>{t('brands.overview.addBranchHint')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-3">
            <div className="w-64">
              <Field label={t('brands.overview.branch')} controlId="brand-branch-picker">
                <Select
                  id="brand-branch-picker"
                  value={pickedBranchId}
                  disabled={branchesQuery.loading}
                  onChange={(e) => setPickedBranchId(e.target.value)}
                >
                  <option value="">
                    {branchesQuery.loading ? t('common.loading') : t('brands.overview.selectBranch')}
                  </option>
                  {candidates.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name} ({branch.code})
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Button onClick={assign} disabled={!pickedBranchId || assigning}>
              {assigning ? t('common.loading') : t('brands.overview.addBranch')}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('brands.overview.branches')}</CardTitle>
        </CardHeader>
        <CardContent>
          {brand.branches.length === 0 ? (
            <EmptyState
              title={t('brands.overview.noBranches')}
              hint={canManage ? t('brands.overview.noBranchesHint') : undefined}
            />
          ) : (
            <ul className="divide-y">
              {brand.branches.map((branch) => (
                <li key={branch.id} className="flex items-center justify-between gap-3 py-3">
                  <div>
                    <p className="font-medium">{branch.name}</p>
                    <p className="text-xs text-ink-muted">{branch.code}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone={branch.isActive ? 'success' : 'neutral'}>
                      {branch.isActive ? t('common.active') : t('common.inactive')}
                    </Badge>
                    {canManage && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(branch)}
                        aria-label={t('brands.overview.removeBranch')}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
